import { useState, useCallback, useEffect } from 'react';
import { api } from '@halo/web-lib';
import {
  AlertTriangle,
  CheckCircle2,
  ListChecks,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';

/* ─── Types ───────────────────────────────────── */

interface QueueStatus {
  queueName: string;
  tasksCount: number;
  oldestTaskAgeSec: number | null;
}

type ActionKind = 'pause' | 'resume' | 'purge';

/* ─── Toast ───────────────────────────────────── */

function useToast() {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  return { toast, showToast: setToast };
}

function ToastBanner({
  message,
  type,
  onDismiss,
}: {
  message: string;
  type: 'success' | 'error';
  onDismiss: () => void;
}) {
  return (
    <div
      className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
        type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
      }`}
    >
      {type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {message}
      <button onClick={onDismiss} className="ml-2 opacity-70 hover:opacity-100">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────── */

function humanizeAge(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 0) return '—';
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins < 60) return `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) return `${hours}h ${remMins}m`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return `${days}d ${remHours}h`;
}

/* ─── Main Component ──────────────────────────── */

export function CloudTasksPage() {
  const [status, setStatus] = useState<QueueStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<ActionKind | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const { toast, showToast } = useToast();

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/ingestion/admin/cloud-tasks/queue');
      if (res.success && res.data) {
        setStatus(res.data as QueueStatus);
        setLastRefresh(Date.now());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 15_000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Auto-refresh countdown
  const [refreshCountdown, setRefreshCountdown] = useState(15);
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshCountdown(Math.max(0, 15 - Math.floor((Date.now() - lastRefresh) / 1000)));
    }, 1000);
    return () => clearInterval(interval);
  }, [lastRefresh]);

  async function handleAction(kind: ActionKind) {
    if (kind === 'purge') {
      if (!window.confirm('Purge ALL pending tasks? This cannot be undone.')) return;
    }
    setActing(kind);
    try {
      const res = await api.post(`/api/v1/ingestion/admin/cloud-tasks/queue/${kind}`, {});
      if (res.success) {
        showToast({ message: `Queue ${kind} succeeded`, type: 'success' });
        await fetchStatus();
      } else {
        console.error(`${kind} failed:`, res);
        showToast({ message: `Queue ${kind} failed`, type: 'error' });
      }
    } catch (err) {
      console.error(`${kind} failed:`, err);
      showToast({ message: `Queue ${kind} failed`, type: 'error' });
    } finally {
      setActing(null);
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  return (
    <div>
      {toast && <ToastBanner message={toast.message} type={toast.type} onDismiss={() => showToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-gray-700 dark:text-gray-200" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Cloud Tasks</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Queue depth and lifecycle controls for the ingestion Cloud Tasks queue.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            {refreshCountdown}s
          </span>
          <button
            onClick={fetchStatus}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Hero block */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-4">
        <div className="flex flex-col gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-1">
              Queue
            </div>
            <div className="font-mono text-sm text-gray-900 dark:text-gray-100">
              {status?.queueName ?? '—'}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-1">
                Pending tasks
              </div>
              <div className="text-4xl font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                {status?.tasksCount ?? 0}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-1">
                Oldest task age
              </div>
              <div className="text-4xl font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                {humanizeAge(status?.oldestTaskAgeSec ?? null)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-3">
          Queue controls
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => handleAction('pause')}
            disabled={acting !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 transition-colors"
          >
            {acting === 'pause' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pause className="w-3.5 h-3.5" />}
            Pause
          </button>
          <button
            onClick={() => handleAction('resume')}
            disabled={acting !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 transition-colors"
          >
            {acting === 'resume' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            Resume
          </button>
          <button
            onClick={() => handleAction('purge')}
            disabled={acting !== null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-colors"
          >
            {acting === 'purge' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Purge
          </button>
        </div>
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3">
          Pause halts dispatch but retains tasks. Resume re-enables dispatch. Purge permanently deletes all pending tasks.
        </p>
      </div>
    </div>
  );
}
